import React, { useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Tooltip, Upload, Input, Popover } from 'antd';
import { 
    BoldOutlined, 
    ItalicOutlined, 
    UnderlineOutlined, 
    StrikethroughOutlined,
    OrderedListOutlined, 
    UnorderedListOutlined, 
    FontSizeOutlined,
    HighlightOutlined,
    LinkOutlined,
    UndoOutlined,
    RedoOutlined,
    LeftOutlined,
    RightOutlined,
    PaperClipOutlined,
    DeleteOutlined,
    FileOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    UpOutlined,
    DownOutlined,
    CommentOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignature } from '@fortawesome/free-solid-svg-icons';
import { useEditor, EditorContent } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
// Individual TipTap extensions (StarterKit is not installed)
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import HardBreak from '@tiptap/extension-hard-break';
import History from '@tiptap/extension-history';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { formatFileSize } from '../../utility/formatters';
import { validateFileType } from '../../utility/fileTypes';
import '../../styles/messages/MessageEditor.css';

// ============================================================================
// CONFIGURATION
// ============================================================================

const createExtensions = (placeholder) => [
    // Core extensions
    Document,
    Paragraph,
    Text,
    HardBreak,
    Dropcursor,
    Gapcursor,
    History.configure({ depth: 100, newGroupDelay: 500 }),
    // Text formatting
    Bold,
    Italic,
    Underline,
    Strike,
    Heading.configure({ levels: [2] }),
    Highlight.configure({
        multicolor: false,
        HTMLAttributes: { class: 'editor-highlight' },
    }),
    Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'editor-link' },
    }),
    // Lists - must include ListItem for lists to work
    ListItem,
    BulletList.configure({
        HTMLAttributes: { class: 'editor-bullet-list' },
    }),
    OrderedList.configure({
        HTMLAttributes: { class: 'editor-ordered-list' },
    }),
    // Placeholder
    Placeholder.configure({ placeholder }),
];

// Toolbar configuration - easily extensible
const TOOLBAR_CONFIG = [
    { type: 'button', command: 'undo', icon: UndoOutlined, labelKey: 'undo', canCheck: false },
    { type: 'button', command: 'redo', icon: RedoOutlined, labelKey: 'redo', canCheck: false },
    { type: 'separator' },
    { type: 'button', command: 'toggleBold', icon: BoldOutlined, labelKey: 'bold', activeKey: 'bold' },
    { type: 'button', command: 'toggleItalic', icon: ItalicOutlined, labelKey: 'italic', activeKey: 'italic' },
    { type: 'button', command: 'toggleUnderline', icon: UnderlineOutlined, labelKey: 'underline', activeKey: 'underline' },
    { type: 'button', command: 'toggleStrike', icon: StrikethroughOutlined, labelKey: 'strikethrough', activeKey: 'strike' },
    { type: 'separator' },
    { type: 'button', command: 'toggleBulletList', icon: UnorderedListOutlined, labelKey: 'unorderedList', activeKey: 'bulletList' },
    { type: 'button', command: 'toggleOrderedList', icon: OrderedListOutlined, labelKey: 'orderedList', activeKey: 'orderedList' },
    { type: 'separator' },
    { type: 'button', command: 'toggleHeading', args: { level: 2 }, icon: FontSizeOutlined, labelKey: 'heading', activeKey: 'heading', activeArgs: { level: 2 } },
    { type: 'button', command: 'toggleHighlight', icon: HighlightOutlined, labelKey: 'highlight', activeKey: 'highlight' },
    { type: 'link', icon: LinkOutlined, labelKey: 'link', activeKey: 'link' },
];

// ============================================================================
// HOOKS
// ============================================================================

const useToolbarScroll = (toolbarRef, forceShowArrows) => {
    const [showLeftArrow, setShowLeftArrow] = useState(forceShowArrows);
    const [showRightArrow, setShowRightArrow] = useState(forceShowArrows);
    
    const updateArrows = useCallback(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) return;

        if (forceShowArrows) {
            setShowLeftArrow(true);
            setShowRightArrow(true);
            return;
        }
        
        const { scrollLeft, scrollWidth, clientWidth } = toolbar;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }, [forceShowArrows, toolbarRef]);

    useEffect(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) return;

        updateArrows();
        const timer = setTimeout(updateArrows, 100);

        if (toolbar.scrollWidth > toolbar.clientWidth) {
            toolbar.scrollLeft = toolbar.scrollWidth - toolbar.clientWidth;
            setTimeout(updateArrows, 50);
        }

        // Convert vertical scroll to horizontal scroll
        const handleWheel = (e) => {
            if (toolbar.scrollWidth > toolbar.clientWidth) {
                e.preventDefault();
                toolbar.scrollLeft += e.deltaY;
            }
        };

        toolbar.addEventListener('scroll', updateArrows);
        toolbar.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('resize', updateArrows);

        return () => {
            clearTimeout(timer);
            toolbar.removeEventListener('scroll', updateArrows);
            toolbar.removeEventListener('wheel', handleWheel);
            window.removeEventListener('resize', updateArrows);
        };
    }, [updateArrows, toolbarRef]);

    const scroll = useCallback((direction) => {
        const toolbar = toolbarRef.current;
        if (!toolbar || toolbar.scrollWidth <= toolbar.clientWidth) return;

        const scrollAmount = Math.floor(toolbar.clientWidth * 0.8) || 100;
        toolbar.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
    }, [toolbarRef]);

    return { showLeftArrow, showRightArrow, scroll };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ToolbarButton = ({ editor, item, t, disabled }) => {
    const { command, args, icon: Icon, labelKey, activeKey, activeArgs, canCheck = true } = item;
    
    const isActive = canCheck && activeKey && editor?.isActive(activeKey, activeArgs);
    const canExecute = command === 'undo' ? editor?.can().undo() : 
                       command === 'redo' ? editor?.can().redo() : true;
    
    const handleClick = () => {
        if (!editor) return;
        const chain = editor.chain().focus();
        if (args) {
            chain[command](args).run();
        } else {
            chain[command]().run();
        }
    };

    return (
        <Tooltip title={t(`messages.send.editor.${labelKey}`)}>
            <Button
                icon={<Icon />}
                onClick={handleClick}
                disabled={disabled || !canExecute}
                className={isActive ? 'toolbar-btn toolbar-btn-active' : 'toolbar-btn'}
                aria-pressed={isActive}
                aria-label={t(`messages.send.editor.${labelKey}`)}
            />
        </Tooltip>
    );
};

const LinkButton = ({ editor, item, t, disabled }) => {
    const [linkUrl, setLinkUrl] = useState('');
    const [popoverOpen, setPopoverOpen] = useState(false);
    const { icon: Icon, labelKey, activeKey } = item;
    
    const isActive = activeKey && editor?.isActive(activeKey);
    
    const handleOpenChange = (open) => {
        if (open) {
            // Pre-fill with existing link URL if there is one
            const existingUrl = editor?.getAttributes('link')?.href || '';
            setLinkUrl(existingUrl);
        }
        setPopoverOpen(open);
    };
    
    const handleSetLink = () => {
        if (!editor) return;
        
        if (linkUrl === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
        }
        setPopoverOpen(false);
        setLinkUrl('');
    };
    
    const handleRemoveLink = () => {
        if (!editor) return;
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        setPopoverOpen(false);
        setLinkUrl('');
    };
    
    const linkContent = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: 250 }}>
            <Input
                placeholder={t('messages.send.editor.linkPlaceholder')}
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onPressEnter={handleSetLink}
                size="small"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {isActive && (
                    <Button size="small" danger onClick={handleRemoveLink}>
                        {t('messages.send.editor.removeLink')}
                    </Button>
                )}
                <Button size="small" type="primary" onClick={handleSetLink}>
                    {t('messages.send.editor.setLink')}
                </Button>
            </div>
        </div>
    );
    
    return (
        <Popover
            content={linkContent}
            title={<div style={{ textAlign: 'center' }}>{t(`messages.send.editor.${labelKey}`)}</div>}
            trigger="click"
            open={popoverOpen}
            onOpenChange={handleOpenChange}
        >
            <Tooltip title={t(`messages.send.editor.${labelKey}`)}>
                <Button
                    icon={<Icon />}
                    disabled={disabled}
                    className={isActive ? 'toolbar-btn toolbar-btn-active' : 'toolbar-btn'}
                    aria-pressed={isActive}
                    aria-label={t(`messages.send.editor.${labelKey}`)}
                />
            </Tooltip>
        </Popover>
    );
};

const SignatureButton = ({ editor, signature, t, disabled }) => {
    if (signature === null) return null;

    const handleClick = () => {
        if (!signature || !editor) return;

        const hasContent = editor.getText().trim().length > 0;
        const signatureHtml = hasContent 
            ? `<p><br></p><p><br></p><p>${signature}</p>`
            : `<p>${signature}</p>`;

        editor.chain()
            .focus()
            .setTextSelection(editor.state.doc.content.size)
            .insertContent(signatureHtml)
            .run();
    };

    return (
        <>
            <div className="toolbar-separator" />
            <Tooltip title={signature ? t('messages.send.editor.addSignature') : t('messages.send.editor.noSignature')}>
                <Button
                    icon={<FontAwesomeIcon icon={faSignature} />}
                    className="toolbar-btn signature-btn"
                    onClick={handleClick}
                    disabled={disabled || !signature}
                    aria-label={t('messages.send.editor.addSignature')}
                />
            </Tooltip>
        </>
    );
};

const Toolbar = ({ editor, signature, disabled, forceShowArrows, t, attachmentsEnabled, attachmentsAllowAdd = true, uploadProps, showDocumensoLabel, formCollapsed, onFormCollapseToggle }) => {
    const toolbarRef = useRef(null);
    const { showLeftArrow, showRightArrow, scroll } = useToolbarScroll(toolbarRef, forceShowArrows);

    return (
        <div className="editor-toolbar">
            {showLeftArrow && (
                <Button
                    icon={<LeftOutlined />}
                    className="toolbar-scroll-btn toolbar-scroll-left"
                    onClick={() => scroll('left')}
                    size="small"
                    type="text"
                />
            )}
            
            <div className="toolbar-content" ref={toolbarRef}>
                {TOOLBAR_CONFIG.map((item, index) => {
                    if (item.type === 'separator') {
                        return <div key={index} className="toolbar-separator" />;
                    }
                    if (item.type === 'link') {
                        return (
                            <LinkButton
                                key="link"
                                editor={editor}
                                item={item}
                                t={t}
                                disabled={disabled}
                            />
                        );
                    }
                    return (
                        <ToolbarButton
                            key={item.command + (item.args?.level || '')}
                            editor={editor}
                            item={item}
                            t={t}
                            disabled={disabled}
                        />
                    );
                })}
                <SignatureButton editor={editor} signature={signature} t={t} disabled={disabled} />
                
                {/* Spacer to push content */}
                <div className="toolbar-spacer" />
            </div>

            {/* Form collapse chevron + Upload - always visible on right, outside scroll area */}
            {(onFormCollapseToggle || (attachmentsEnabled && uploadProps)) && (
                <div className="toolbar-upload-group toolbar-upload-group-fixed">
                    {onFormCollapseToggle && (
                        <Tooltip title={formCollapsed ? t('messages.send.showFormFields') : t('messages.send.hideFormFields')}>
                            <Button
                                icon={formCollapsed ? <DownOutlined /> : <UpOutlined />}
                                onClick={onFormCollapseToggle}
                                className="toolbar-btn toolbar-collapse-btn"
                                aria-label={formCollapsed ? 'Show form fields' : 'Hide form fields'}
                            />
                        </Tooltip>
                    )}
                    {attachmentsEnabled && attachmentsAllowAdd && uploadProps && (
                        <>
                            {showDocumensoLabel && (
                                <span className="toolbar-documenso-label">Powered by Documenso</span>
                            )}
                            <Upload {...uploadProps} disabled={disabled}>
                                <Button 
                                    icon={<PaperClipOutlined />} 
                                    disabled={disabled}
                                    className="toolbar-upload-btn"
                                >
                                    {t('messages.send.editor.uploadFile')}
                                </Button>
                            </Upload>
                        </>
                    )}
                </div>
            )}

            {showRightArrow && (
                <Button
                    icon={<RightOutlined />}
                    className="toolbar-scroll-btn toolbar-scroll-right"
                    onClick={() => scroll('right')}
                    size="small"
                    type="text"
                />
            )}
        </div>
    );
};

// Attachments Panel Component
const AttachmentsPanel = ({
    fileList,
    onFileListChange,
    allowedFileTypes,
    maxUploadSize,
    disabled,
    t,
    onAttachmentClick,
    onCommentClick,
    attachmentPreviewable,
    fileHasFieldsMap,
    setValuesLabel,
    allowRemove = true,
    fileCommentsCountMap = {},
}) => {
    const handleRemoveFile = (uid, e) => {
        e?.stopPropagation();
        const newFileList = fileList.filter(item => item.uid !== uid);
        onFileListChange(newFileList);
    };

    return (
        <>
            <div className="attachments-info-row">
                <span className="attachments-allowed">
                    {t('messages.send.editor.allowedTypes', { types: allowedFileTypes || '' })}
                </span>
                <span className="attachments-size-limit">
                    {t('messages.send.editor.maxSize', { size: maxUploadSize || 10 })}
                </span>
            </div>

            <div className="attachments-list">
                {fileList.length === 0 ? (
                    <div className="attachments-empty">
                        {t('messages.send.editor.noAttachments')}
                    </div>
                ) : (
                    fileList.map((file, index) => {
                        const hasFields = fileHasFieldsMap?.[index] !== false;
                        const canPreviewAttachment = attachmentPreviewable ? attachmentPreviewable(file) : true;
                        const showSetValuesOverlay = onAttachmentClick && setValuesLabel && !hasFields && !disabled && canPreviewAttachment;
                        const commentCount = fileCommentsCountMap[file.uid] ?? 0;
                        return (
                            <div
                                key={file.uid}
                                className={`attachment-item ${onAttachmentClick && canPreviewAttachment ? 'attachment-item-clickable' : ''} ${showSetValuesOverlay ? 'attachment-item-with-overlay' : ''}`}
                                onClick={onAttachmentClick && canPreviewAttachment ? () => onAttachmentClick(file, index) : undefined}
                            >
                                <div className="attachment-info">
                                    <FileOutlined className="attachment-icon" />
                                    <span className="attachment-name" title={file.name}>
                                        {file.name}
                                    </span>
                                    <span className="attachment-size">
                                        {formatFileSize(file.size ?? file.originFileObj?.size ?? file.file_size ?? 0)}
                                    </span>
                                    {(commentCount > 0 || (onCommentClick && canPreviewAttachment)) && (
                                        <span
                                            className={`attachment-comment-badge ${onCommentClick && canPreviewAttachment ? 'attachment-comment-badge-clickable' : ''}`}
                                            onClick={onCommentClick && canPreviewAttachment ? (e) => { e.stopPropagation(); onCommentClick(file, index); } : undefined}
                                            role={onCommentClick && canPreviewAttachment ? 'button' : undefined}
                                            title={onCommentClick && canPreviewAttachment ? (t('attachmentPreview.addCommentLabel') || 'Add comment') : undefined}
                                        >
                                            <CommentOutlined />
                                            {commentCount > 0 && <span className="attachment-comment-count">{commentCount}</span>}
                                        </span>
                                    )}
                                </div>
                                {allowRemove && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={(e) => handleRemoveFile(file.uid, e)}
                                    className="attachment-remove"
                                    disabled={disabled}
                                />
                                )}
                                {showSetValuesOverlay && (
                                    <div className="attachment-set-values-overlay">
                                        <span className="attachment-set-values-label">{setValuesLabel}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

        </>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MessageEditor = forwardRef(({ 
    value, 
    onChange, 
    placeholder = '', 
    disabled = false, 
    signature = null, 
    forceShowArrows = false,
    // Attachment props
    attachmentsEnabled = false,
    allowedFileTypes = '',
    maxUploadSize = 10,
    onFileListChange,
    showDocumensoLabel = false,
    initialFileList = [],
    showError = () => {},
    onAttachmentClick,
    onCommentClick,
    attachmentPreviewable,
    fileHasFieldsMap,
    setValuesLabel,
    attachmentsAllowAdd = true,
    attachmentsAllowRemove = true,
    fileCommentsCountMap = {},
    // Form collapse (optional - for TTLSend, etc.)
    formCollapsed,
    onFormCollapseToggle,
}, ref) => {
    const { t } = useTranslation();
    const isUpdatingRef = useRef(false);
    const [fileList, setFileList] = useState(initialFileList);
    const [attachmentsCollapsed, setAttachmentsCollapsed] = useState(false);
    const [attachmentsPanelWidth, setAttachmentsPanelWidth] = useState(() => {
        const saved = localStorage.getItem('editor_attachments_width');
        return saved ? parseFloat(saved) : 30;
    });
    const [isResizing, setIsResizing] = useState(false);
    const editorBodyRef = useRef(null);
    
    const effectivePlaceholder = placeholder || t('messages.send.editor.placeholder');

    const editor = useEditor({
        extensions: createExtensions(effectivePlaceholder),
        content: value || '',
        editable: !disabled,
        onUpdate: ({ editor }) => {
            if (onChange) {
                isUpdatingRef.current = true;
                onChange(editor.getHTML());
                requestAnimationFrame(() => {
                    isUpdatingRef.current = false;
                });
            }
        },
    });
    
    // Handle file list changes
    const handleFileListChange = useCallback((newFileList) => {
        setFileList(newFileList);
        if (onFileListChange) {
            onFileListChange(newFileList);
        }
    }, [onFileListChange]);

    // Handle file upload validation
    const handleBeforeUpload = useCallback(async (file) => {
        const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
        const allowedTypes = allowedFileTypes?.split(',') || [];
        const maxSize = (maxUploadSize || 10) * 1024 * 1024;

        if (file.size >= maxSize) {
            showError(t('messages.validation.fileTooLarge', { size: maxUploadSize || 10 }));
            return Upload.LIST_IGNORE;
        }

        if (!allowedTypes.includes(fileExtension)) {
            showError(t('messages.validation.fileTypeNotSupported', { types: allowedFileTypes || '' }));
            return Upload.LIST_IGNORE;
        }

        try {
            const isValidType = await validateFileType(file, allowedTypes);
            if (!isValidType) {
                showError(t('messages.validation.invalidFileType'));
                return Upload.LIST_IGNORE;
            }
        } catch (error) {
            showError(t('messages.validation.fileValidationError'));
            return Upload.LIST_IGNORE;
        }

        return false;
    }, [allowedFileTypes, maxUploadSize, showError, t]);

    // Upload props for the toolbar
    const uploadProps = {
        beforeUpload: handleBeforeUpload,
        fileList,
        onChange: ({ fileList: newFileList }) => handleFileListChange(newFileList),
        showUploadList: false,
        multiple: true,
    };

    // Handle panel resize mouse events
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !editorBodyRef.current) return;
            
            const containerRect = editorBodyRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            
            // Calculate content width percentage, then derive attachments width
            const contentWidthPercent = (mouseX / containerWidth) * 100;
            let newAttachmentsWidth = 100 - contentWidthPercent;
            // Constrain between 20% and 50%
            newAttachmentsWidth = Math.max(20, Math.min(50, newAttachmentsWidth));
            
            setAttachmentsPanelWidth(newAttachmentsWidth);
            localStorage.setItem('editor_attachments_width', newAttachmentsWidth.toString());
        };

        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('editor-resizing-panels');
            }
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('editor-resizing-panels');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('editor-resizing-panels');
        };
    }, [isResizing]);

    // Toggle attachments panel
    const toggleAttachments = useCallback(() => {
        setAttachmentsCollapsed(prev => !prev);
    }, []);

    // Sync initial file list (for limited edit / display-only: when parent passes files, show them)
    useEffect(() => {
        if (initialFileList && Array.isArray(initialFileList) && initialFileList.length > 0) {
            setFileList(initialFileList);
            // Auto-expand panel when display-only files arrive so they are visible
            if (!attachmentsAllowAdd) {
                setAttachmentsCollapsed(false);
            }
        }
    }, [initialFileList, initialFileList?.length, attachmentsAllowAdd]);

    // Expose editor instance and file list via ref
    useImperativeHandle(ref, () => ({
        getEditor: () => editor,
        focus: () => editor?.commands.focus(),
        getHTML: () => editor?.getHTML() || '',
        getText: () => editor?.getText() || '',
        isEmpty: () => editor?.isEmpty ?? true,
        // Attachment methods
        getFileList: () => fileList,
        setFileList: (files) => handleFileListChange(files),
        clearFileList: () => handleFileListChange([]),
    }), [editor, fileList, handleFileListChange]);

    // Sync value prop with editor content
    useEffect(() => {
        if (!editor || isUpdatingRef.current) return;
        
        const currentHtml = editor.getHTML();
        if (value !== currentHtml && value !== undefined) {
            const { from, to } = editor.state.selection;
            editor.commands.setContent(value || '');
            
            requestAnimationFrame(() => {
                const docSize = editor.state.doc.content.size;
                if (from <= docSize && to <= docSize) {
                    editor.commands.setTextSelection({ 
                        from: Math.min(from, docSize), 
                        to: Math.min(to, docSize) 
                    });
                }
            });
        }
    }, [editor, value]);

    // Update editable state
    useEffect(() => {
        if (editor) {
            editor.setEditable(!disabled);
        }
    }, [editor, disabled]);

    // Update placeholder
    useEffect(() => {
        if (editor) {
            editor.extensionManager.extensions.forEach((ext) => {
                if (ext.name === 'placeholder') {
                    ext.options.placeholder = effectivePlaceholder;
                }
            });
            editor.view.updateState(editor.state);
        }
    }, [editor, effectivePlaceholder]);

    if (!editor) return null;

    const showAttachmentsPanel = attachmentsEnabled && !attachmentsCollapsed;

    const editorContent = (
        <div className={`message-editor ${disabled ? 'disabled' : ''} ${attachmentsEnabled ? 'attachments-enabled' : ''} ${showAttachmentsPanel ? 'with-attachments' : ''}`}>
            <Toolbar 
                editor={editor} 
                signature={signature} 
                disabled={disabled}
                forceShowArrows={forceShowArrows}
                t={t}
                attachmentsEnabled={attachmentsEnabled}
                attachmentsAllowAdd={attachmentsAllowAdd}
                uploadProps={uploadProps}
                showDocumensoLabel={showDocumensoLabel}
                formCollapsed={formCollapsed}
                onFormCollapseToggle={onFormCollapseToggle}
            />
            
            <div className="editor-body" ref={editorBodyRef}>
                <div 
                    className="editor-content-wrapper"
                    style={showAttachmentsPanel ? { width: `calc(${100 - attachmentsPanelWidth}% - 6px)` } : undefined}
                >
                    <EditorContent 
                        editor={editor}
                        className="editor-content"
                        onClick={() => !disabled && editor.commands.focus()}
                    />
                    
                    {/* Attachments toggle button */}
                    {attachmentsEnabled && (
                        <Tooltip title={attachmentsCollapsed ? t('messages.send.editor.showAttachments') : t('messages.send.editor.hideAttachments')}>
                            <Button
                                type="text"
                                size="small"
                                icon={attachmentsCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={toggleAttachments}
                                className="attachments-toggle-btn"
                                aria-label={attachmentsCollapsed ? 'Show attachments' : 'Hide attachments'}
                            />
                        </Tooltip>
                    )}
                </div>

                {showAttachmentsPanel && (
                    <>
                        {/* Resizable separator */}
                        <div 
                            className="editor-resize-handle"
                            style={{ right: `${attachmentsPanelWidth}%` }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setIsResizing(true);
                            }}
                        />
                        <div 
                            className="editor-attachments-panel"
                            style={{ width: `${attachmentsPanelWidth}%` }}
                        >
                            <div className="attachments-panel-content">
                                <AttachmentsPanel
                                    fileList={fileList}
                                    onFileListChange={handleFileListChange}
                                    allowedFileTypes={allowedFileTypes}
                                    maxUploadSize={maxUploadSize}
                                    disabled={disabled}
                                    t={t}
                                    onAttachmentClick={onAttachmentClick}
                                    onCommentClick={onCommentClick}
                                    attachmentPreviewable={attachmentPreviewable}
                                    fileHasFieldsMap={fileHasFieldsMap}
                                    setValuesLabel={setValuesLabel}
                                    allowRemove={attachmentsAllowRemove}
                                    fileCommentsCountMap={fileCommentsCountMap}
                                />
            </div>
                </div>
                    </>
            )}
            </div>
        </div>
    );
    
    if (forceShowArrows) {
        return (
            <div className="message-editor-document-wrapper">
                {editorContent}
            </div>
        );
    }
    
    return editorContent;
});

MessageEditor.displayName = 'MessageEditor';

export default MessageEditor; 
